// ================================
// src/components/TopBar.tsx — FINAL (No global RTL, layout stays LTR)
// - 문서 <html dir> 변경 ❌  → 전체 뒤집힘 방지
// - 텍스트는 dir="auto"로 자연 방향 표시
// - 드롭다운/TopBar 레이아웃은 항상 LTR 고정
// - safe-area + (옵션) StatusBar 동적 import는 그대로 유지
// ================================
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import ZuzuLogo from "../assets/zuzu-logo.png";

const LANG_LABEL: Record<string, string> = {
  en: "English",
  ko: "한국어",
  ja: "日本語",
  "zh-CN": "简体中文",
  "zh-TW": "繁體中文",
  vi: "Tiếng Việt",
  hi: "हिन्दी",
  id: "Indonesia",
  th: "ไทย",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  pt: "Português",
  ru: "Русский",
  ar: "العربية",
  tr: "Türkçe",
};

function getSupportedInOrder(i18n: any): string[] {
  const cfgList: string[] = i18n?.options?.supportedLngs || [];
  if (cfgList.length) return cfgList; // i18n.ts의 순서 그대로
  const store = i18n?.services?.resourceStore?.data ?? {};
  return Object.keys(store).filter((k) => !["cimode", "dev"].includes(k));
}

const TopBar: React.FC = () => {
  const { i18n, t } = useTranslation();
  const current = i18n.resolvedLanguage || i18n.language || "en";
  const SUPPORTED = getSupportedInOrder(i18n);

  // ❌ 더 이상 문서 전체 dir 변경하지 않음 (전체 UI 뒤집힘 방지)
  // 필요하면 theme/locale 표시는 data-attr로만 남김
  useEffect(() => {
    document.documentElement.setAttribute("data-lang", current);
  }, [current]);

  // (옵션) 네이티브 상태바 오버레이 해제 — 웹에서는 시도 안 함
  useEffect(() => {
    (async () => {
      if (!Capacitor.isNativePlatform()) return;
      try {
        const pluginName = "@capacitor/status-bar";
        const { StatusBar } = await import(/* @vite-ignore */ pluginName);
        await StatusBar.setOverlaysWebView({ overlay: false });
      } catch {}
    })();
  }, []);

  return (
    <div className="tb-root">
      <div className="tb-wrap">
        <Link to="/signal-intro" className="brand">
          <img src={ZuzuLogo} alt="ZUZU" />
          <span className="title" dir="auto">
            <span className="title-full">ZUZU </span>
            <span className="title-short">ZUZU </span>
          </span>
        </Link>

        <div className="right">
          <a
            href="https://zuzusignal.com"
            className="link"
            target="_blank"
            rel="noopener noreferrer"
            dir="auto"
          >
            {t("nav.zuzusignal", "zuzusignal")}
          </a>

          <label
            htmlFor="lang-select"
            style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}
          >
            Language
          </label>

          {/* ✅ 드롭다운 자체는 물리 LTR 고정 (열림/정렬 뒤집힘 방지) */}
          <select
            id="lang-select"
            className="lang"
            value={current}
            onChange={(e) => i18n.changeLanguage(e.target.value)}
          >
            {SUPPORTED.map((code) => (
              <option key={code} value={code} dir="auto">
                {LANG_LABEL[code] ?? code.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      </div>

      <style>{`
:root{
  --safe-top: env(safe-area-inset-top, 0px);
  --safe-right: env(safe-area-inset-right, 0px);
  --safe-left: env(safe-area-inset-left, 0px);
}

/* ✅ TopBar 레이아웃은 항상 LTR */
.tb-root{
  direction: ltr;
  position: sticky;
  top: 0;
  z-index: 20;
  background: #0a0d14;
  box-shadow: 0 1px 0 rgba(255,255,255,0.06);
  padding-left: var(--safe-left);
  padding-right: var(--safe-right);
}
.tb-wrap{
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 56px;
  padding: calc(6px + var(--safe-top)) 12px 8px;
  box-sizing: border-box;
}
.is-native .tb-wrap{ max-width: none; margin: 0; }

.brand{
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: #eaeaea;
  font-weight: 900;
  flex: 0 1 auto;
  min-width: 0;
}
.brand img{ height: 28px; width: auto; flex: 0 0 auto; }
.title{ white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.title-full{ display: inline; }
.title-short{ display: none; }
@media (max-width:640px){ .title-full{display:none;} .title-short{display:inline;} }

/* 항상 우측 끝 */
.right{
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.link{ color: #c9d8ff; text-decoration: none; }
.link:hover{ text-decoration: underline; }

/* ✅ 드롭다운은 물리 LTR, 옵션 텍스트만 dir=auto */
.lang{
  direction: ltr;         /* 드롭다운/팝업의 기준 방향 고정 */
  text-align: left;
  background: #0f1420;
  border: 1px solid #ffffff22;
  color: #eaeaea;
  border-radius: 8px;
  padding: 6px 10px;
  max-width: 220px;
}
@media (max-width:380px){
  .brand img{ height: 24px; }
  .lang{ max-width: 160px; }
}

/* ⛔ 전역 RTL로 레이아웃을 바꾸는 규칙은 두지 마세요
   (예: :root[dir="rtl"] .app { flex-direction: row-reverse; } 등) */
      `}</style>
    </div>
  );
};

export default TopBar;
